import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/** Modes de dossier, tels que l'API les nomme. */
export type ModeDossier = 'CFA' | 'OF';

export type CriterePiece =
  | 'AUCUN'
  | 'HEBERGE_PAR_TIERS'
  | 'RQTH'
  | 'DEJA_APPRENTI'
  | 'DEMANDEUR_EMPLOI'
  | 'TITULAIRE_DIPLOME';

export interface ReglePieceParametree {
  code: string;
  libelle: string;
  type: string;
  modes: ModeDossier[];
  obligatoire: boolean;
  critere: CriterePiece;
  condition: string | null;
  nombreAttendu: number;
  nombreMaximum: number;
  formats: string[];
  libelleFormats: string;
  ordre: number;
  actif: boolean;
  /** Pièce livrée avec la plateforme : elle se désactive, elle ne se supprime pas. */
  socle: boolean;
  dossiers: number;
  fichiersDeposes: number;
}

export interface EtatParametragePieces {
  regles: ReglePieceParametree[];
  formats: { code: string; libelle: string; extensions: string[] }[];
  criteres: { code: CriterePiece; libelle: string }[];
}

/** Champs modifiables d'une pièce. Le code n'en fait pas partie : il est figé. */
export interface EntreeReglePiece {
  libelle: string;
  modes: ModeDossier[];
  obligatoire: boolean;
  critere: CriterePiece;
  condition: string | null;
  nombreAttendu: number;
  nombreMaximum: number;
  formats: string[];
  actif: boolean;
}

export type EntreeCreationReglePiece = EntreeReglePiece & { code: string };

const CLE = ['parametrage-pieces'];

export function useParametragePieces() {
  return useQuery({
    queryKey: CLE,
    queryFn: () => appelApi<EtatParametragePieces>('/parametrage-pieces'),
  });
}

/**
 * Toute écriture renvoie le paramétrage complet et resynchronise les dossiers
 * ouverts côté serveur : les écrans qui affichent des pièces ou une complétude
 * repartent des nouvelles règles, pas du cache précédent.
 */
function useEcriture<TEntree>(
  requete: (entree: TEntree) => Promise<EtatParametragePieces>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: requete,
    onSuccess: (etat) => {
      client.setQueryData(CLE, etat);
      void client.invalidateQueries({ queryKey: ['referentiels'] });
      void client.invalidateQueries({ queryKey: ['apprenants'] });
      void client.invalidateQueries({ queryKey: ['dossier'] });
      void client.invalidateQueries({ queryKey: ['espace-apprenant'] });
    },
  });
}

export function useCreerReglePiece() {
  return useEcriture((entree: EntreeCreationReglePiece) =>
    appelApi<EtatParametragePieces>('/parametrage-pieces', { methode: 'POST', corps: entree }),
  );
}

export function useModifierReglePiece() {
  return useEcriture(({ code, ...entree }: EntreeCreationReglePiece) =>
    appelApi<EtatParametragePieces>(`/parametrage-pieces/${encodeURIComponent(code)}`, {
      methode: 'PATCH',
      corps: entree,
    }),
  );
}

export function useSupprimerReglePiece() {
  return useEcriture((code: string) =>
    appelApi<EtatParametragePieces>(`/parametrage-pieces/${encodeURIComponent(code)}`, {
      methode: 'DELETE',
    }),
  );
}

export function useReordonnerReglesPieces() {
  return useEcriture((codes: string[]) =>
    appelApi<EtatParametragePieces>('/parametrage-pieces/ordre', {
      methode: 'PUT',
      corps: { codes },
    }),
  );
}
